import React, { useCallback, useEffect, useState } from "react";
import { Button, Form, Input, Spin, message } from "antd";
import { ArrowLeftOutlined, LockOutlined, SafetyCertificateOutlined } from "@ant-design/icons";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { persistAuthenticatedSession } from "../../Actions/user.action";
import { confirm_password_reset, validate_password_reset } from "../../Services/user.service";
import { VERDICTIA_WORDMARK_DARK_SRC } from "../../brand/verdictiaBrand";
import { consumePasswordResetTokenFromUrl } from "../../utils/passwordResetToken";
import "./password-reset.css";

const { Password } = Input;

function ResetPassword() {
    const { t } = useTranslation("login");
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [token, setToken] = useState(null);
    const [status, setStatus] = useState("checking");
    const [loading, setLoading] = useState(false);
    const [passwordStrength, setPasswordStrength] = useState(0);

    const validateToken = useCallback(async (rawToken) => {
        if (!rawToken) {
            setStatus("invalid");
            return;
        }

        setStatus("checking");
        try {
            const result = await validate_password_reset(rawToken);
            setStatus(result?.data?.valid ? "valid" : "invalid");
        } catch {
            setStatus("invalid");
        }
    }, []);

    useEffect(() => {
        const rawToken = consumePasswordResetTokenFromUrl(window);
        setToken(rawToken);
        validateToken(rawToken);
    }, [validateToken]);

    const calculatePasswordStrength = (value = "") => {
        let strength = 0;
        if (value.length >= 8) strength += 1;
        if (/[A-Z]/.test(value)) strength += 1;
        if (/[0-9]/.test(value)) strength += 1;
        if (/[^A-Za-z0-9]/.test(value)) strength += 1;
        return strength;
    };

    const handlePasswordChange = (event) => {
        setPasswordStrength(calculatePasswordStrength(event.target.value));
    };

    const handleSubmit = async (values) => {
        if (!token) {
            setStatus("invalid");
            return;
        }

        setLoading(true);
        try {
            const result = await confirm_password_reset(
                token,
                values.newPassword,
                values.confirmPassword
            );
            const responseData = result?.data || {};
            if (responseData.email_verification_required) {
                form.resetFields();
                setPasswordStrength(0);
                setStatus("verification_required");
                message.success(t("reset.confirm.success_verification_required"));
                return;
            }

            if (!responseData.access_token) {
                message.error(t("reset.confirm.error"));
                return;
            }

            persistAuthenticatedSession(dispatch, responseData);
            try {
                sessionStorage.setItem("verdictia_auth_fade", "1");
            } catch {
                // no-op
            }
            message.success(t("reset.confirm.success"));
            navigate("/workspace", { replace: true });
        } catch (error) {
            if (error?.response?.status === 400) {
                setStatus("invalid");
                message.error(t("reset.confirm.invalid"));
            } else if (error?.response?.status === 429) {
                message.error(t("reset.request.rate_limited"));
            } else {
                message.error(t("reset.confirm.error"));
            }
        } finally {
            setLoading(false);
        }
    };

    const passwordRules = [
        { required: true, message: t("reset.confirm.rules.required") },
        { min: 8, message: t("reset.confirm.rules.min") },
        { pattern: /[A-Z]/, message: t("reset.confirm.rules.uppercase") },
        { pattern: /[0-9]/, message: t("reset.confirm.rules.number") },
        { pattern: /[^A-Za-z0-9]/, message: t("reset.confirm.rules.special") },
    ];

    return (
        <main className="auth-reset-page">
            <section className="auth-reset-card" aria-labelledby="reset-password-title">
                <button className="auth-reset-back" type="button" onClick={() => navigate("/")}>
                    <ArrowLeftOutlined />
                    <span>{t("reset.back_to_login")}</span>
                </button>

                <img className="auth-reset-logo" src={VERDICTIA_WORDMARK_DARK_SRC} alt="Verdictia" />

                <div className="auth-reset-copy">
                    <p className="auth-reset-kicker">{t("reset.confirm.kicker")}</p>
                    <h1 id="reset-password-title">{t("reset.confirm.title")}</h1>
                    <p>{t("reset.confirm.subtitle")}</p>
                </div>

                {status === "checking" && (
                    <div className="auth-reset-loading" role="status">
                        <Spin />
                    </div>
                )}

                {status === "invalid" && (
                    <div className="auth-reset-invalid" role="alert">
                        <SafetyCertificateOutlined />
                        <p>{t("reset.confirm.invalid")}</p>
                        <Button type="default" onClick={() => navigate("/forgot-password")}>
                            {t("reset.confirm.request_new")}
                        </Button>
                    </div>
                )}

                {status === "verification_required" && (
                    <div className="auth-reset-status auth-reset-status--verification" role="status">
                        <p>{t("reset.confirm.verification_required")}</p>
                        <Button type="primary" onClick={() => navigate("/")}>
                            {t("reset.back_to_login")}
                        </Button>
                    </div>
                )}

                {status === "valid" && (
                    <Form
                        form={form}
                        className="auth-reset-form"
                        layout="vertical"
                        onFinish={handleSubmit}
                        autoComplete="off"
                    >
                        <Form.Item
                            label={t("reset.confirm.password_label")}
                            name="newPassword"
                            rules={passwordRules}
                        >
                            <Password
                                size="large"
                                prefix={<LockOutlined />}
                                placeholder={t("reset.confirm.password_placeholder")}
                                onChange={handlePasswordChange}
                            />
                        </Form.Item>

                        <div className="auth-reset-strength" aria-hidden="true">
                            <span style={{ width: `${(passwordStrength / 4) * 100}%` }} />
                        </div>

                        <Form.Item
                            label={t("reset.confirm.confirm_label")}
                            name="confirmPassword"
                            rules={[
                                { required: true, message: t("reset.confirm.rules.confirm") },
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        if (!value || getFieldValue("newPassword") === value) {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject(new Error(t("reset.confirm.rules.mismatch")));
                                    },
                                }),
                            ]}
                        >
                            <Password
                                size="large"
                                prefix={<LockOutlined />}
                                placeholder={t("reset.confirm.confirm_placeholder")}
                            />
                        </Form.Item>

                        <Button
                            className="auth-reset-primary"
                            type="primary"
                            size="large"
                            htmlType="submit"
                            block
                            loading={loading}
                        >
                            {t("reset.confirm.submit")}
                        </Button>
                    </Form>
                )}
            </section>
        </main>
    );
}

export default ResetPassword;
